import womanIllustration from "../assets/custom-dev-illustration.webp";
import illustration from "../assets/dev-illustration.webp";
import centralGroup from "../assets/woman-grouped.webp";
import badgeReturn from "../assets/badge-return.png";
import badgeIntegration from "../assets/badge-integrate.png";
import badgeSupport from "../assets/badge-support.png";
import badgeUnique from "../assets/badge-unique.png";
import badgeScale from "../assets/badge-scale.png";
import badgeProductivity from "../assets/badge-productivity.png";
import { motion } from "framer-motion";
import underline from "../assets/underline.png";
import { Helmet } from "react-helmet-async";
import efficiencyIcon from "../assets/efficiencyy.png";
import securityIcon from "../assets/security.png";
import integrationIcon from "../assets/integrationn.png";

export default function CustomDevelopmentSection() {
  const fadeUpVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.6,
        ease: "easeOut",
      },
    }),
  };

  const featureData = [
    {
      icon: efficiencyIcon,
      title: "Efficiency",
      description:
        "With a custom solution, developers can analyze the places where things slow down",
    },
    {
      icon: securityIcon,
      title: "Security",
      description:
        "When they identify a vulnerability in a program, they can exploit it in multiple organizations",
    },
    {
      icon: integrationIcon,
      title: "Integration",
      description:
        "Easy integration is a feature that will dramatically increase the performance of a business.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Expert Software Consulting Services | Accelerate Digital
          Transformation
        </title>
        <meta
          name="description"
          content="Boost your business growth with expert software consulting. Our senior consultants provide strategic guidance, optimized systems, and flexible solutions for smarter decision-making and accelerated digital transformation."
        />
        <meta
          name="keywords"
          content="IT strategy consulting, technology consulting, business technology solutions, software project management, software optimization, IT consulting experts, strategic software solutions"
        />
      </Helmet>
      <section className="bg-white font-poppins overflow-hidden">
        <section className="bg-[#F8F8F8] font-poppins overflow-hidden">
          <div className="max-w-7xl mx-auto font-poppins px-6 py-20 grid grid-cols-12 items-center gap-8">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="col-span-12 md:col-span-6"
            >
              <p className="text-sm font-semibold font-poppins tracking-wider text-[#07862B] uppercase mb-4">
                Custom Development
              </p>
              <h2 className="text-4xl font-bold font-poppins text-black mb-6 leading-tight">
                Built exactly how <br /> you need it
              </h2>
              <p className="text-gray-700 text-base leading-relaxed max-w-lg">
                We build tailored software solutions designed to fit your unique
                business needs. From concept to launch, our expert developers
                deliver scalable, high-performance systems. Partner with us to
                turn your ideas into powerful, custom-built applications.
              </p>
            </motion.div>

            {/* Right Illustration */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="col-span-12 md:col-span-6 relative"
            >
              <img
                src={womanIllustration}
                alt="Custom development illustration"
                className="w-full max-w-[450px] mx-auto"
              />
            </motion.div>
          </div>

          {/* Overview Section */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-[#07862B] text-white font-poppins py-10 px-4 sm:px-8 md:px-16 lg:px-24"
          >
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              {/* Left Side */}
              <div className="relative">
                <h2 className="text-3xl sm:text-4xl font-bold uppercase">
                  Overview
                </h2>
                <img
                  src={underline}
                  alt="underline"
                  className="absolute left-0 bottom-[-10px] w-24 sm:w-28"
                />
              </div>

              {/* Right Side */}
              <p className="text-[16px] font-normal leading-relaxed text-[#FFFFFF] max-w-3xl">
                Development builds strategic partnerships, while business
                development drives client acquisition and growth. Software
                consulting and custom development provide expert, adaptive
                solutions across evolving industry needs.
              </p>
            </div>
          </motion.section>

          {/* What is custom development */}
          <motion.section
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.4 }} // amount: how much needs to be visible
            className="w-full bg-white py-[80px] px-4 font-poppins"
          >
            <div className="max-w-[1200px] mx-auto flex flex-col lg:flex-row items-center gap-[60px]">
              {/* Left: Image */}
              <div className="flex-shrink-0">
                <img
                  src={illustration}
                  alt="Custom Development Illustration"
                  className="w-full max-w-[550px] object-contain"
                />
              </div>

              {/* Right: Text */}
              <div className="flex-1 text-left">
                <h2 className="text-[#1A1A1A] text-[40px] font-semibold leading-[52px] mb-3">
                  What is custom
                  <br />
                  development?
                </h2>
                <div className="w-[48px] h-[2px] bg-[#2AA244] mb-6"></div>
                <p className="text-[#1A1A1A] text-[16px] leading-[28px] font-normal max-w-[540px]">
                  Custom development is the process of creating software
                  specifically designed to meet the unique needs of a business
                  or industry. Unlike off-the-shelf solutions, it builds
                  applications tailored to fit a company’s workflows, goals, and
                  challenges. This approach offers greater flexibility,
                  scalability, and efficiency.
                </p>
              </div>
            </div>
          </motion.section>
        </section>

        <section className="relative bg-[#FCF5DC] font-poppins overflow-hidden py-20 px-4">
          {/* Heading */}
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-[36px] sm:text-[42px] lg:text-[48px] font-bold text-[#1A1A1A] leading-tight mb-4">
              Advantages of custom <br className="hidden sm:block" />{" "}
              development
            </h2>
            <p className="text-[#3D3D3D] text-[16px] leading-relaxed">
              It creates software tailored to a business’s unique needs and
              offers personalized
              <br className="hidden md:block" />
              solutions that evolve with changing requirements.
            </p>
          </div>

          {/* Mobile/Tablet: Grid layout */}
          <div className="block lg:hidden content-center max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              badgeReturn,
              badgeIntegration,
              badgeSupport,
              badgeUnique,
              badgeScale,
              badgeProductivity,
            ].map((badge, idx) => (
              <motion.img
                key={idx}
                src={badge}
                alt={`Badge ${idx}`}
                className="w-[280px] sm:w-[300px]"
                animate={{ y: [0, -6, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 3 + idx * 0.1,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>

          {/* Desktop: Absolute layout */}
          <div className="hidden lg:block relative w-full max-w-[1440px] min-h-[600px] mx-auto">
            {/* Central Group Image */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <img
                src={centralGroup}
                alt="Central Group"
                className="w-[590px] max-w-full"
              />
            </div>

            {/* Left Badges */}
            <motion.img
              src={badgeReturn}
              alt="Return"
              className="absolute left-[6%] top-[60px] w-[300px] xl:w-[360px]"
              animate={{ y: [0, -6, 0] }}
              transition={{
                duration: 3.2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.img
              src={badgeIntegration}
              alt="Integration"
              className="absolute left-[0%] top-[230px] w-[300px] xl:w-[360px]"
              animate={{ y: [0, -6, 0] }}
              transition={{
                duration: 3.4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.img
              src={badgeSupport}
              alt="Support"
              className="absolute left-[5%] bottom-[60px] w-[300px] xl:w-[360px]"
              animate={{ y: [0, -6, 0] }}
              transition={{
                duration: 3.6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Right Badges */}
            <motion.img
              src={badgeUnique}
              alt="Unique"
              className="absolute right-[6%] top-[60px] w-[300px] xl:w-[360px]"
              animate={{ y: [0, -6, 0] }}
              transition={{
                duration: 3.1,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.img
              src={badgeScale}
              alt="Scale"
              className="absolute right-[4%] top-[230px] w-[300px] xl:w-[360px]"
              animate={{ y: [0, -6, 0] }}
              transition={{
                duration: 3.3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.img
              src={badgeProductivity}
              alt="Productivity"
              className="absolute right-[5%] bottom-[60px] w-[300px] xl:w-[360px]"
              animate={{ y: [0, -6, 0] }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>
        </section>
        <section className="w-full bg-white font-poppins py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-[1280px] mx-auto text-center mb-16">
            <h2 className="text-[36px] sm:text-[42px] lg:text-[48px] font-bold text-[#1A1A1A] leading-tight mb-4">
              Benefits of using custom <br className="hidden sm:block" />
              development
            </h2>
            <p className="text-[#333] text-lg max-w-[720px] mx-auto">
              Unlock the full potential of your business with powerful,
              tailor-made software solutions!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {featureData.map((feature, index) => (
              <motion.div
                key={feature.title}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeUpVariant}
                className={`flex flex-col items-center text-center px-4 ${
                  index < 2 ? "md:border-r md:border-gray-200" : ""
                }`}
              >
                <div className="mb-6 relative">
                  <img
                    src={feature.icon}
                    alt={feature.title}
                    className="w-[72px] h-[72px] object-contain relative z-10"
                  />
                  <div className="absolute top-2 left-2 w-14 h-14 bg-[#B8F6C8] rounded-full -z-0"></div>
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#444] text-base leading-relaxed max-w-[300px]">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>
      </section>
    </>
  );
}
